/**
 * Parses Department Builder CSV rows into `assignments` (batch submit) and `accountTree` (preview).
 * CSV columns: Account Name, Account HCC ID, Parent Account HCC ID, Department Name, Start Date, End Date (optional column; blank cell = no end date / open-ended)
 *
 * Account hierarchy: one top-level account, up to 2 levels of child accounts (3 tiers total).
 * Top account may be given as an explicit row (blank Parent Account HCC ID) or omitted when it exists
 * only in HRP: if every row has a parent, the single Parent HCC ID that never appears as Account HCC ID
 * on any row is treated as the implicit top account (no department row submitted for it).
 * Otherwise Parent Account HCC ID must match another row's Account HCC ID.
 * Department names must be unique per account; the same name may appear on different accounts.
 */

/** Required header names (normalized). End Date may be omitted entirely for open-ended assignments. */
const REQUIRED_HEADERS = [
    "account name",
    "account hcc id",
    "parent account hcc id",
    "department name",
    "start date",
];

const OPTIONAL_HEADER_END_DATE = "end date";

/** @param {string} [h] CSV header cell @returns {string} */
const normalizeHeader = (h) =>
    String(h ?? "")
        .replace(/^\uFEFF/, "")
        .trim()
        .toLowerCase();

/** @param {unknown} v @returns {string | null} trimmed string or null if empty */
const normalizeCell = (v) => {
    const s = String(v ?? "").trim();
    return s === "" ? null : s;
};

/**
 * @param {unknown} value
 * @returns {{ iso: string, raw: string } | { iso: null, raw: string } | null}
 */
const parseFlexibleDate = (value) => {
    if (value == null || String(value).trim() === "") return null;
    const raw = String(value).trim();
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        return { iso: null, raw };
    }
    return { iso: d.toISOString().slice(0, 10), raw };
};

/**
 * Stable key for an account in the CSV (parent HCC ID + account HCC ID).
 * Top-level uses empty string for parent segment.
 * @param {string | null} parentAccountHccId
 * @param {string} accountHccId
 */
const accountRowKey = (parentAccountHccId, accountHccId) => {
    const p = parentAccountHccId ?? "";
    return `${p}\0${accountHccId}`;
};

/**
 * One row in the payload sent to the department submit API (one department assignment).
 * @typedef {object} DepartmentAssignment
 * @property {string} accountName
 * @property {string} accountHccId
 * @property {string | null} parentAccountHccId
 * @property {string} departmentName
 * @property {string | null} startDate ISO date (yyyy-mm-dd) or null if unparseable
 * @property {string | null} endDate ISO date (yyyy-mm-dd) or null if unparseable
 */

/**
 * @param {Record<string, string>[]} rows - Papa-parse rows with header keys
 * @returns {{ ok: boolean, errors: string[], data: { assignments: DepartmentAssignment[], accountTree: object | null } | null }}
 */
export const parseDepartmentBuilderCsv = (rows) => {
    const errors = [];

    if (!Array.isArray(rows) || rows.length === 0) {
        return { ok: false, errors: ["CSV has no data rows."], data: null };
    }

    const first = rows[0];
    const headerMap = {};
    for (const key of Object.keys(first)) {
        const n = normalizeHeader(key);
        if (REQUIRED_HEADERS.includes(n) || n === OPTIONAL_HEADER_END_DATE) {
            headerMap[n] = key;
        }
    }

    for (const h of REQUIRED_HEADERS) {
        if (!headerMap[h]) {
            errors.push(
                `Missing required column "${h.replace(/\b\w/g, (c) => c.toUpperCase())}" (check spelling).`
            );
        }
    }

    if (errors.length) {
        return { ok: false, errors, data: null };
    }

    const col = {
        accountName: headerMap["account name"],
        accountHccId: headerMap["account hcc id"],
        parentAccountHccId: headerMap["parent account hcc id"],
        departmentName: headerMap["department name"],
        startDate: headerMap["start date"],
        /** Present only if the CSV includes an End Date column. */
        endDate: headerMap[OPTIONAL_HEADER_END_DATE],
    };

    /** @type {Map<string, { accountName: string, accountHccId: string, parentAccountHccId: string | null, departments: object[], _deptNames: Set<string> }>} */
    const byKey = new Map();

    /** @type {DepartmentAssignment[]} */
    const assignments = [];

    /** Parent account HCC ID -> child account HCC IDs in order of first appearance (all rows, mirrors prior second-pass scan). */
    const childrenByParentHcc = new Map();
    const noteChild = (parentHccId, childHccId) => {
        if (!childrenByParentHcc.has(parentHccId)) childrenByParentHcc.set(parentHccId, []);
        const list = childrenByParentHcc.get(parentHccId);
        if (!list.includes(childHccId)) list.push(childHccId);
    };

    /** Non-null parent HCC IDs referenced on any row (for implicit top detection). */
    const allReferencedParentIds = new Set();

    rows.forEach((row, idx) => {
        const line = idx + 2;
        const accountName = normalizeCell(row[col.accountName]);
        const accountHccId = normalizeCell(row[col.accountHccId]);
        const parentAccountHccId = normalizeCell(row[col.parentAccountHccId]);
        if (parentAccountHccId) {
            allReferencedParentIds.add(parentAccountHccId);
        }
        if (accountHccId && parentAccountHccId) {
            noteChild(parentAccountHccId, accountHccId);
        }
        const departmentName = normalizeCell(row[col.departmentName]);
        const start = parseFlexibleDate(row[col.startDate]);
        const end =
            col.endDate != null ? parseFlexibleDate(row[col.endDate]) : null;

        if (!accountName) {
            errors.push(`Row ${line}: Account Name is required.`);
            return;
        }
        if (!accountHccId) {
            errors.push(`Row ${line}: Account HCC ID is required.`);
            return;
        }
        if (!departmentName) {
            errors.push(`Row ${line}: Department Name is required.`);
            return;
        }

        const key = accountRowKey(parentAccountHccId, accountHccId);
        if (!byKey.has(key)) {
            byKey.set(key, {
                accountName,
                accountHccId,
                parentAccountHccId,
                departments: [],
                _deptNames: new Set(),
            });
        }

        const entry = byKey.get(key);
        if (entry.accountName !== accountName) {
            errors.push(`Row ${line}: Internal key conflict for account HCC ID "${accountHccId}" (Account Name mismatch).`);
            return;
        }
        if (entry.parentAccountHccId !== parentAccountHccId) {
            errors.push(
                `Row ${line}: Account "${accountName}" (${accountHccId}) must use the same Parent Account HCC ID in every row (expected "${entry.parentAccountHccId ?? ""}").`
            );
            return;
        }

        const deptLower = departmentName.toLowerCase();
        if (entry._deptNames.has(deptLower)) {
            errors.push(
                `Row ${line}: Duplicate department "${departmentName}" for account "${accountName}" (departments must be unique per account).`
            );
            return;
        }
        entry._deptNames.add(deptLower);

        entry.departments.push({
            departmentName,
            startDate: start?.iso,
            endDate: end?.iso,
            startDateRaw: start?.raw ?? null,
            endDateRaw: end?.raw ?? null,
        });

        assignments.push({
            accountName,
            accountHccId,
            parentAccountHccId,
            departmentName,
            startDate: start?.iso ?? null,
            endDate: end?.iso ?? null,
        });
    });

    const initialExplicitRoots = [...byKey.values()].filter((a) => a.parentAccountHccId == null);
    const accountHccIdsFromRows = new Set([...byKey.values()].map((a) => a.accountHccId));

    /** Parent HCC IDs that appear on rows but never as an Account HCC ID — candidate external top in HRP. */
    const implicitCandidates = [...allReferencedParentIds].filter((p) => !accountHccIdsFromRows.has(p));

    let usedImplicitRoot = false;

    if (initialExplicitRoots.length > 1) {
        errors.push(
            `Multiple top-level accounts found (${initialExplicitRoots.map((r) => r.accountName).join(", ")}); only one is allowed.`
        );
    } else if (initialExplicitRoots.length === 1) {
        // Bad parent refs (not in CSV) are caught in the unknown-parent loop below.
    } else {
        // No row with blank parent — try exactly one implicit top (HRP-only account).
        if (implicitCandidates.length === 0) {
            errors.push(
                'No top-level account: add a row with an empty "Parent Account HCC ID", or use sub-account rows only if exactly one "Parent Account HCC ID" is referenced that never appears as "Account HCC ID" (your HRP top account).'
            );
        } else if (implicitCandidates.length > 1) {
            errors.push(
                `Multiple implicit top accounts (ambiguous): ${implicitCandidates.join(", ")} are referenced as parents but have no Account row. Only one HRP top account is allowed.`
            );
        } else {
            const implicitRootHccId = implicitCandidates[0];
            usedImplicitRoot = true;
            byKey.set(accountRowKey(null, implicitRootHccId), {
                accountName: implicitRootHccId,
                accountHccId: implicitRootHccId,
                parentAccountHccId: null,
                departments: [],
                _deptNames: new Set(),
            });
        }
    }

    const roots = [...byKey.values()].filter((a) => a.parentAccountHccId == null);

    if (roots.length === 0 && errors.length === 0) {
        errors.push('Internal error: could not determine a single top-level account.');
    } else if (roots.length > 1 && errors.length === 0) {
        errors.push(
            `Multiple top-level accounts found (${roots.map((r) => r.accountName).join(", ")}); only one is allowed.`
        );
    }

    const firstParent = normalizeCell(rows[0][col.parentAccountHccId]);
    const firstAccount = normalizeCell(rows[0][col.accountName]);
    if (!usedImplicitRoot) {
        if (firstParent != null) {
            errors.push(
                'The first data row must be the top-level account with a blank "Parent Account HCC ID".'
            );
        }
        if (roots.length === 1 && firstAccount && roots[0].accountName !== firstAccount) {
            errors.push(
                `First row account "${firstAccount}" must match the single top-level account "${roots[0].accountName}".`
            );
        }
    }

    const accountHccIds = new Set([...byKey.values()].map((a) => a.accountHccId));

    for (const acc of byKey.values()) {
        if (acc.parentAccountHccId == null) continue;
        if (!accountHccIds.has(acc.parentAccountHccId)) {
            errors.push(
                `Account "${acc.accountName}" (${acc.accountHccId}) references unknown Parent Account HCC ID "${acc.parentAccountHccId}" (no row uses that value as Account HCC ID).`
            );
        }
    }

    /**
     * One display line per department for the nested tree preview.
     * @param {{ departmentName: string, startDate?: string|null, endDate?: string|null, startDateRaw?: string|null, endDateRaw?: string|null }} d
     */
    const formatDepartmentForTree = (d) => {
        const start = d.startDate ?? d.startDateRaw ?? "";
        const end = d.endDate ?? d.endDateRaw ?? "";
        const endLabel = end === "" ? "—" : end;
        return `${d.departmentName} - Start date: ${start} End date: ${endLabel}`;
    };

    /**
     * @param {string} accountHccId
     * @param {string | null} parentAccountHccId
     * @param {number} depth 0 = top account, 1–2 = nested tiers
     */
    const buildTree = (accountHccId, parentAccountHccId, depth) => {
        if (depth > 2) {
            errors.push(
                `Account HCC ID "${accountHccId}" exceeds maximum depth (allowed: top account plus two levels of sub-accounts).`
            );
            return null;
        }

        const key = accountRowKey(parentAccountHccId, accountHccId);
        const entry = byKey.get(key);
        if (!entry) {
            errors.push(
                `Missing account definition for HCC ID "${accountHccId}" under parent "${parentAccountHccId ?? "(top)"}".`
            );
            return null;
        }

        const childHccIds = childrenByParentHcc.get(accountHccId) ?? [];
        const subAccounts = {};
        for (const childHccId of childHccIds) {
            const childKey = accountRowKey(accountHccId, childHccId);
            if (!byKey.has(childKey)) continue;
            const childEntry = byKey.get(childKey);
            const childNode = buildTree(childHccId, accountHccId, depth + 1);
            if (childNode) subAccounts[childEntry.accountName] = childNode;
        }

        return {
            departments: entry.departments.map(formatDepartmentForTree),
            ...(Object.keys(subAccounts).length > 0 ? { subAccounts } : {}),
        };
    };

    let accountTree = null;
    if (roots.length === 1 && errors.length === 0) {
        const root = roots[0];
        const rootNode = buildTree(root.accountHccId, null, 0);
        if (rootNode) {
            accountTree = { [root.accountName]: rootNode };
        }
    }

    if (errors.length > 0) {
        accountTree = null;
    }

    const data = {
        /** One object per CSV row — batch submit payload. */
        assignments,
        /** Nested structure for `AccountTreePreview` only. */
        accountTree,
    };

    const ok = errors.length === 0 && data.accountTree != null;
    return { ok, errors, data };
};
